import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader,
  Save,
  Unlink,
  Users,
  Archive,
} from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { kanbanTeamColor } from '../../utils/kanbanTeamColors';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../ConfirmationModal';
import { KanbanListRoleBadge } from '../kanban/KanbanListRoleBadge';
import { KanbanPriorityBadge } from '../kanban/KanbanPriorityBadge';
import type {
  IncidentKanbanLink,
  IncidentWorkspaceSummary,
  UpdateIncidentKanbanLinkRequest,
} from '../../types/incident';

const SYNC_FLAGS: Array<{
  key: keyof Pick<
    IncidentKanbanLink,
    'sync_status' | 'sync_priority' | 'sync_assignees' | 'sync_comments' | 'propagate_watchers'
  >;
  label: string;
  hint: string;
}> = [
  {
    key: 'sync_status',
    label: 'Status',
    hint: 'Status changes propagate; cards move to the mapped list, incidents update from list role.',
  },
  {
    key: 'sync_priority',
    label: 'Priority',
    hint: 'Priority (1–10) propagates in both directions.',
  },
  {
    key: 'sync_assignees',
    label: 'Assignees',
    hint: 'Assignee add/remove propagates across the bridge.',
  },
  {
    key: 'sync_comments',
    label: 'Comments',
    hint: 'New comments are mirrored to the other side with a [via …] prefix.',
  },
  {
    key: 'propagate_watchers',
    label: 'Watchers',
    hint: 'New watchers on either side become watchers on the other (one-way per event, symmetric overall).',
  },
];

export interface IncidentBridgeRowProps {
  incidentId: string;
  summary: IncidentWorkspaceSummary;
  canManage: boolean;
  onChanged: () => void;
}

/**
 * One row inside `<IncidentWorkspacesSection>` — board+card snapshot, sync flag toggles,
 * notes editor, and unlink action for a single bridge link.
 */
export const IncidentBridgeRow = ({
  incidentId,
  summary,
  canManage,
  onChanged,
}: IncidentBridgeRowProps) => {
  const initialLink = summary.link;
  const [link, setLink] = useState<IncidentKanbanLink>(initialLink);
  const [notes, setNotes] = useState<string>(initialLink.notes ?? '');
  const [expanded, setExpanded] = useState(false);
  const [savingFlags, setSavingFlags] = useState<Record<string, boolean>>({});
  const [savingNotes, setSavingNotes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync local state when the parent reloads after a WS event.
  useEffect(() => {
    setLink(summary.link);
    setNotes(summary.link.notes ?? '');
  }, [summary.link]);

  const teamColor = useMemo(() => kanbanTeamColor(summary.board.team), [summary.board.team]);

  const cardHref = `/workspaces/${encodeURIComponent(summary.board.id)}?card=${encodeURIComponent(
    summary.card.id,
  )}`;
  const boardHref = `/workspaces/${encodeURIComponent(summary.board.id)}`;

  const notesDirty = notes !== (link.notes ?? '');

  const handleToggleFlag = async (key: typeof SYNC_FLAGS[number]['key']) => {
    if (!canManage) return;
    const next = link[key] === 1 ? false : true;
    const optimistic: IncidentKanbanLink = { ...link, [key]: next ? 1 : 0 };
    setLink(optimistic);
    setSavingFlags((s) => ({ ...s, [key]: true }));
    setError(null);
    try {
      const body: UpdateIncidentKanbanLinkRequest = { [key]: next };
      const updated = await incidentsApiService.updateKanbanLink(
        incidentId,
        summary.card.id,
        body,
      );
      setLink(updated);
      onChanged();
    } catch (err) {
      // Roll back the optimistic toggle on failure.
      setLink(link);
      setError(err instanceof Error ? err.message : `Failed to update ${key}`);
    } finally {
      setSavingFlags((s) => ({ ...s, [key]: false }));
    }
  };

  const handleSaveNotes = async () => {
    if (!canManage || !notesDirty) return;
    setSavingNotes(true);
    setError(null);
    try {
      const updated = await incidentsApiService.updateKanbanLink(
        incidentId,
        summary.card.id,
        { notes: notes.trim() === '' ? null : notes },
      );
      setLink(updated);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUnlink = async () => {
    setDeleting(true);
    setError(null);
    try {
      await incidentsApiService.deleteKanbanLink(incidentId, summary.card.id);
      setConfirmDelete(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink');
      setDeleting(false);
    }
  };

  return (
    <div className="subcard overflow-hidden">
      {/* Top row */}
      <div className="flex items-start gap-2 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="btn-icon btn-icon-sm -ml-1"
          title={expanded ? 'Collapse' : 'Expand'}
          aria-label={expanded ? 'Collapse bridge details' : 'Expand bridge details'}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={cardHref}
              className="text-sm font-semibold text-white hover:text-accent-300 transition-colors truncate"
              title={summary.card.title}
            >
              {summary.card.title}
            </Link>
            <KanbanListRoleBadge role={summary.card.list_role} />
            {summary.card.priority != null && summary.card.priority !== 5 && (
              <KanbanPriorityBadge priority={summary.card.priority} />
            )}
            {summary.card.is_archived === 1 && (
              <span className="badge badge-neutral">
                <Archive className="w-3 h-3" /> Archived
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1 text-xs text-glass-400 flex-wrap">
            <Link to={boardHref} className="hover:text-accent-300 transition-colors truncate">
              {summary.board.title}
            </Link>
            <span
              className="chip"
              style={{
                backgroundColor: `${teamColor}20`,
                borderColor: `${teamColor}40`,
                color: teamColor,
              }}
            >
              {summary.board.team}
            </span>
            <span>· {summary.card.list_title}</span>
            <span className="inline-flex items-center gap-1 tabular-nums" title="Assignees on the card">
              <Users className="w-3 h-3" />
              {summary.card.assignees_count}
            </span>
            <span className="text-glass-500 tabular-nums">
              Linked {formatDistanceToNow(new Date(link.linked_at), { addSuffix: true })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Link
            to={cardHref}
            className="btn-icon btn-icon-sm"
            title="Open card in workspace"
            aria-label="Open card in workspace"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
          {canManage && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
              title="Unlink this card"
              aria-label="Unlink this card"
            >
              <Unlink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-3 border-t border-glass-200/10 space-y-3">
          {/* Sync flag chips */}
          <div>
            <p className="eyebrow mb-1.5">
              Sync flags
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SYNC_FLAGS.map((flag) => {
                const on = link[flag.key] === 1;
                const busy = savingFlags[flag.key];
                return (
                  <button
                    key={flag.key}
                    type="button"
                    onClick={() => void handleToggleFlag(flag.key)}
                    disabled={!canManage || busy}
                    title={flag.hint}
                    aria-pressed={on}
                    className={cn(
                      'badge transition-colors',
                      on
                        ? 'badge-accent hover:border-accent-500/50'
                        : 'badge-neutral hover:text-white hover:border-glass-200/25',
                      (!canManage || busy) && 'opacity-60 cursor-not-allowed',
                    )}
                  >
                    {busy && <Loader className="w-3 h-3 animate-spin" />}
                    {flag.label}
                    <span className="font-mono text-[10px] opacity-70">{on ? 'on' : 'off'}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-glass-500 mt-1.5">
              Sync only fires when callers use the <span className="font-mono">*-with-sync</span>{' '}
              endpoint variants. Argus does that automatically when any flag is on.
            </p>
          </div>

          {/* Notes */}
          <div>
            <p className="eyebrow mb-1.5">
              Notes
            </p>
            <div className="flex items-start gap-2">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!canManage}
                rows={2}
                placeholder="Optional context for this bridge…"
                className="glass-input flex-1 text-xs"
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => void handleSaveNotes()}
                  disabled={!notesDirty || savingNotes}
                  className="btn-icon"
                  title="Save notes"
                  aria-label="Save notes"
                >
                  {savingNotes ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger text-xs py-2" role="alert">
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmationModal
          isOpen={confirmDelete}
          onClose={() => (deleting ? undefined : setConfirmDelete(false))}
          onConfirm={handleUnlink}
          title="Unlink card?"
          message={`This will remove the bridge between this incident and the card "${summary.card.title}". The card and the incident will not be deleted.`}
          confirmText="Unlink"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
